import s from './DealCard.scss'
import { fromConstantPropertyToSelectValue } from 'utils/serializers'
import cn from 'classnames'
import { Link } from 'react-router-dom'
import { Clients } from './Clients'
import { BoardCardTasks } from 'components/common/BoardCardTasks'
import { Users } from './Users'
import {
  FaCheckSquare,
  FaRegFilePowerpoint,
  FaCaretDown,
  FaCaretUp,
  FaCertificate
} from 'react-icons/fa'
import { FcBullish } from 'react-icons/fc'
import { APP_CONSTANTS } from 'utils/constants'
import { DateInfo } from './DateInfo'
import { timezoneDate, sortDealCompaniesByAwarded } from 'utils/helpers'
import { getCurrency } from 'utils/authService'
import numeral from 'numeral'
import { getSettings } from 'utils/settingsService'
import { useSelector } from 'react-redux'

const DealCard = ({
  name,
  health,
  id,
  status,
  dealCompanies,
  proposals = [],
  todayTasks = [],
  lateTasks = [],
  futureTasks = [],
  completedTasks = [],
  users = [],
  dueDate,
  wonDate,
  lostDate,
  followUpDate,
  dealProjectStatus,
  firstTaskDueDate,
  nextProposalDueDate,
  internalBidStatus,
  bidStatus,
  sealedBid,
  hardBid,
  awardedCompany,
  navigateTo,
  updateDealFollowupDate,
  selected,
  toggleSelectDeal,
  laneId,
  dealsStatuses,
  index,
  quoteNumber,
  updatedAt,
  expanded,
  expandCard,
  grossProfit,
  value,
  cost,
  addedToForecast,
  opportunityAge,
  margin
}) => {
  const checkMarkClass = lateTasks.length
    ? 'late'
    : todayTasks.length
      ? 'today'
      : futureTasks.length
        ? 'future'
        : completedTasks.length
          ? 'completed'
          : 'noTasks'

  const toggleShowMore = e => {
    e.preventDefault()
    expandCard(laneId, id, expanded)
  }

  const settings = useSelector(getSettings)

  return (
    <Link to={`/opportunities/${id}`} className={s.link}>
      <div className={s.card} data-cy={`card-${laneId}-${index}`}>
        <DateInfo
          id={id}
          status={status}
          followUpDate={followUpDate}
          dueDate={dueDate}
          updateDealFollowupDate={updateDealFollowupDate}
          dealsStatuses={dealsStatuses}
          wonDate={wonDate}
          lostDate={lostDate}
        />
        <div className={s.opportunityAge} title="Opportunity Age">
          {opportunityAge}
        </div>
        {settings?.dealSettings?.showProjectStatus &&
        dealProjectStatus &&
        ['won', 'project'].includes(
          status.category?.code.toLowerCase() ?? dealsStatuses[status].category.code.toLowerCase()
        ) ? (
          <div className={cn(s.projectStatus, s.badge, s.pullLeft)}>{dealProjectStatus.name}</div>
        ) : (
          <div
            className={cn(s.health, s.badge, s.pullLeft, {
              [s.slipping]: health === 'SLIPPING',
              [s.warning]: health === 'WARNING'
            })}
          >
            {health !== 'ONTRACK'
              ? fromConstantPropertyToSelectValue(health, 'DealHealth')?.label.split(' ')[0]
              : fromConstantPropertyToSelectValue(health, 'DealHealth')?.label}
          </div>
        )}
        <FaCheckSquare
          className={cn(s.pullRight, s.checkMark, {
            [s.selected]: selected
          })}
          onClick={e => toggleSelectDeal(e, laneId, id)}
        />
        {awardedCompany && (
          <FaCertificate className={cn(s.awardIcon, s.pullRight)} title="Opportunity Awarded" />
        )}
        {addedToForecast && (
          <FcBullish className={cn(s.forecastIcon, s.pullRight)} title="Added to Forecast" />
        )}
        <div className={s.clearfix} />
        <div className={s.name}>{name}</div>
        <div className={s.clearfix} />
        <div className={s.profitInfo}>
          <div title="Value">
            {value
              ? numeral(value).format(
                  getCurrency().symbol +
                    (+value < 1000 ? '0,0[.]00' : +value < 1000000 ? '0.a' : '0.0a')
                )
              : 'N/A'}
          </div>
          <div title="Cost" className={s.cost}>
            {cost
              ? numeral(cost).format(
                  getCurrency().symbol +
                    (+value < 1000 ? '0,0[.]00' : +value < 1000000 ? '0.a' : '0.0a')
                )
              : 'N/A'}
          </div>
          <div title="Gross Profit" className={s.grossProfit}>
            {grossProfit
              ? numeral(grossProfit).format(
                  getCurrency().symbol +
                    (+value < 1000 ? '0,0[.]00' : +value < 1000000 ? '0.a' : '0.0a')
                )
              : 'N/A'}
          </div>
          <div title="Margin" className={s.margin}>
            {margin ? margin + '%' : 'N/A'}
          </div>
        </div>
        {expanded && (
          <>
            <div className={s.footer}>
              <div className={s.clients}>
                <Clients
                  dealId={id}
                  clients={sortDealCompaniesByAwarded(dealCompanies, awardedCompany)}
                />
              </div>
              {quoteNumber && (
                <>
                  <div className={s.clearfix} />
                  <div className={cn(s.awarded, s.pullLeft)}>Quote: {quoteNumber} &bull;&nbsp;</div>
                </>
              )}
              <div className={cn(s.awarded, s.pullLeft)}>Updated: {timezoneDate(updatedAt)}</div>
              <div className={s.clearfix} />
              {awardedCompany && (
                <div className={s.awarded}>
                  Awarded to:
                  <span
                    className={s.clientLink}
                    onClick={e =>
                      navigateTo(e, `/companies/${awardedCompany.id || awardedCompany.value}`)
                    }
                  >
                    {' '}
                    {awardedCompany.name || awardedCompany.label}
                  </span>
                </div>
              )}
              <div className={s.clearfix} />
              <div className={cn(s.pullLeft, s.documentsAndProposals)}>
                <FaRegFilePowerpoint /> <span>{proposals.length}</span>
              </div>
              <Users users={users} />
              <div className={s.clearfix} />
              <BoardCardTasks
                todayTasks={todayTasks}
                lateTasks={lateTasks}
                futureTasks={futureTasks}
                completedTasks={completedTasks}
              />
              <div className={s.clearfix} />
              <div className={s.datesWrapper}>
                {nextProposalDueDate && (
                  <div className={cn(s.nextDate, s.pullLeft)}>
                    next proposal due on <b>{timezoneDate(nextProposalDueDate)}</b>
                  </div>
                )}
                {firstTaskDueDate && (
                  <div className={cn(s.nextDate, s.pullLeft)}>
                    next task due on{' '}
                    <b className={s[checkMarkClass]}>{timezoneDate(firstTaskDueDate)}</b>
                  </div>
                )}
              </div>
              <div className={s.clearfix} />
              <div className={s.dealInfoWrapper}>
                {internalBidStatus && (
                  <div className={cn(s.pullLeft, s.dealInfo)}>
                    <span>{internalBidStatus.name}</span>|
                  </div>
                )}
                {bidStatus && (
                  <div className={cn(s.pullLeft, s.dealInfo)}>
                    <span>{bidStatus.name}</span>
                  </div>
                )}
                {hardBid && (
                  <div className={cn(s.pullLeft, s.dealInfo)}>
                    |<span>Hard Bid</span>{' '}
                  </div>
                )}
                {sealedBid && (
                  <div className={cn(s.pullLeft, s.dealInfo)}>
                    |<span>Sealed Bid</span>{' '}
                  </div>
                )}
              </div>
              <div className={s.clearfix} />
              {proposals.map(proposal => (
                <p className={s.proposals} key={proposal.id}>
                  <FaRegFilePowerpoint />
                  <a
                    className={s.clientLink}
                    onClick={e => {
                      e.preventDefault()
                      window.open(`/proposals/${proposal.id}`, '_blank')
                    }}
                  >
                    <b>{APP_CONSTANTS.ProposalStatus[proposal.status]}</b>- {proposal.companyName}
                  </a>
                </p>
              ))}
            </div>
          </>
        )}
        {expanded ? (
          <FaCaretUp className={s.showMore} onClick={e => toggleShowMore(e)} />
        ) : (
          <FaCaretDown onClick={e => toggleShowMore(e)} className={s.showMore} />
        )}
      </div>
    </Link>
  )
}

export default DealCard
